import {
  AlertAcceptanceStatus,
  CompanyProfile,
  StatusKeyTypes,
} from '@shared/types'
import { AuditResponse } from '@shared/types'
import { GroupEvaluationProfile, Status, StatusTypes } from '@shared/types'
import { apiJSON } from '@shared/utils/api/api.util'
import { useMutation, useQueryClient } from 'react-query'
import { TableRequirement } from './requirements-table.component'
import { cloneDeep, update } from 'lodash'
import { useMemo } from 'react'

export function useRequirementMutation(
  companyID: number,
  auditID?: number,
  statuses?: Status[] | null
) {
  const queryClient = useQueryClient()

  const dontShowStatus = useMemo(
    () => statuses?.find((status) => status.label === StatusTypes.NoShow),
    [statuses]
  )

  const { mutateAsync: hideRequirement } = useMutation(
    async ({ requirement }: { requirement: TableRequirement }) => {
      if (!auditID) {
        return
      }

      const endpoint = `companies/${companyID}/audits/${auditID}`
      await queryClient.cancelQueries(endpoint)

      const previousAudit = queryClient.getQueryData<AuditResponse>(endpoint)
      if (previousAudit && dontShowStatus) {
        previousAudit.scopedRequirements =
          previousAudit.scopedRequirements?.map((scope) => {
            if (scope.id !== requirement.scopeId) {
              return scope
            }

            const updatedScope = cloneDeep(scope)

            return update(
              updatedScope,
              `groups`,
              (groups: GroupEvaluationProfile[]) =>
                groups.map((group) => {
                  if (group.id !== requirement.groupId) {
                    return group
                  }

                  return {
                    ...group,
                    requirements: group.requirements.map((req) => {
                      if (req.id !== requirement.id) {
                        return req
                      }

                      return {
                        ...req,
                        [StatusKeyTypes.AUDITOR_STATUS]: {
                          comment: null,
                          statusBaseId: dontShowStatus.statusID,
                        },
                        auditorAssessment: null,
                        isDraft: false,
                      }
                    }),
                  }
                })
            )
          }) || []
        await queryClient.setQueryData(endpoint, previousAudit)
      }

      return await apiJSON(
        `companies/${companyID}/audits/${auditID}/requirements/${requirement.id}/hide`,
        {},
        { method: 'PUT' }
      )
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(
          `companies/${companyID}/audits/${auditID}`
        )
      },
    }
  )

  const { mutateAsync: hideGroup } = useMutation(
    async ({ groupId, scopeId }: { groupId: number; scopeId: number }) => {
      const endpoint = `companies/${companyID}/audits/${auditID}`
      await queryClient.cancelQueries(endpoint)
      const dontShowStatus = statuses?.find(
        (status) => status.label === StatusTypes.NoShow
      )

      const previousAudit = queryClient.getQueryData<AuditResponse>(endpoint)
      if (previousAudit && dontShowStatus) {
        previousAudit.scopedRequirements =
          previousAudit.scopedRequirements?.map((scope) => {
            if (scope.id !== scopeId) {
              return scope
            }

            const updatedScope = cloneDeep(scope)

            return update(
              updatedScope,
              `groups`,
              (groups: GroupEvaluationProfile[]) =>
                groups.map((group) => {
                  if (group.id !== groupId) {
                    return group
                  }

                  return {
                    ...group,
                    requirements: group.requirements.map((req) => {
                      return {
                        ...req,
                        [StatusKeyTypes.AUDITOR_STATUS]: {
                          comment: null,
                          statusBaseId: dontShowStatus.statusID,
                        },
                        auditorAssessment: null,
                        isDraft: false,
                      }
                    }),
                  }
                })
            )
          }) || []
        await queryClient.setQueryData(endpoint, previousAudit)
      }

      return await apiJSON(
        `companies/${companyID}/audits/${auditID}/groups/${groupId}/hide`,
        {},
        { method: 'PUT' }
      )
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(
          `companies/${companyID}/audits/${auditID}`
        )
      },
    }
  )

  const { mutateAsync: acceptAlert } = useMutation(
    async ({
      requirement,
      isActive,
    }: {
      requirement: TableRequirement
      isActive: boolean
    }) => {
      // NOTE: only on preview mode
      const endpoint = `CompaniesEvaluationProfiles/${companyID}`
      await queryClient.cancelQueries(endpoint)

      const previousCompanyProfile =
        queryClient.getQueryData<CompanyProfile>(endpoint)
      if (previousCompanyProfile) {
        previousCompanyProfile[isActive ? 'assessedScopes' : 'scopesInEsqula'] =
          previousCompanyProfile[
            isActive ? 'assessedScopes' : 'scopesInEsqula'
          ].map((scope) => {
            if (scope.id !== requirement.scopeId) {
              return scope
            }

            const updatedScope = cloneDeep(scope)

            return update(
              updatedScope,
              `groups`,
              (groups: GroupEvaluationProfile[]) =>
                groups.map((group) => {
                  if (group.id !== requirement.groupId) {
                    return group
                  }

                  return {
                    ...group,
                    requirements: group.requirements.map((req) => {
                      if (req.id !== requirement.id) {
                        return req
                      }

                      return {
                        ...req,
                        alert: {
                          ...req.alert,
                          acceptanceStatus: AlertAcceptanceStatus.Accepted,
                        },
                      }
                    }),
                  }
                })
            )
          })

        await queryClient.setQueryData(endpoint, previousCompanyProfile)
      }

      return await apiJSON(
        `companies/${companyID}/${isActive ? 'publishedAuditRequirements' : 'EsqulaRequirements'}/${requirement.id}/acceptAlert`,
        {},
        { method: 'POST' }
      )
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(
          `CompaniesEvaluationProfiles/${companyID}`
        )
      },
    }
  )

  return { hideRequirement, hideGroup, acceptAlert, dontShowStatus }
}
